import { jsxs, jsx } from 'react/jsx-runtime';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { useFloating, offset } from '@floating-ui/react';
import { IconOther12DragHandle } from '@fabric-app/icons/svg';
import { ButtonIcon } from '@fabric-app/ui/components/Button';
import { keyframes, styled } from 'styled-components';
import { useState, useRef } from 'react';
import { getNodeAtPos, getElementOffset, cleanupDragPreviews, createDragPreview } from '../utils/dragHandleUtils.js';
import { DragHandlePlugin, DragHandlePluginKey, CustomTextSelection } from '../utils/dragHandlePlugin.js';
import { useDebounce } from '@fabric-app/hooks/useDebounce';

const IS_DEBUG = false;
const DEFAULT_OFFSET = 10;
const fadeIn = keyframes([
  `from{opacity:0;}to{opacity:1;}`
]);
const Handle = styled.div.withConfig({
  displayName: "DragHandle__Handle",
  componentId: "sc-613b4c0a-0"
})([
  `z-index:1;position:absolute;left:0;top:0;animation:`,
  ` 0.2s ease-in-out;user-select:none !important;&::after{content:'';display:block;position:absolute;top:-10%;height:120%;left:100%;width:10px;z-index:-1;}`
], fadeIn);
const DragButton = styled(ButtonIcon).withConfig({
  displayName: "DragHandle__DragButton",
  componentId: "sc-613b4c0a-1"
})([
  `width:min-content;padding:0.14rem;border-radius:0.3rem;cursor:grab;`
]);
styled.div.withConfig({
  displayName: "DragHandle__DebugText",
  componentId: "sc-613b4c0a-2"
})([
  `position:absolute;left:0;top:100%;font-size:10px;color:#ff0000;background:rgba(255,255,255,0.8);border-radius:2px;padding:2px 4px;margin-top:2px;text-align:center;white-space:nowrap;z-index:100;`
]);
const DragHandle = ({ editor }) => {
  const [state, setState] = useState({
    currentPos: null,
    elementOffset: DEFAULT_OFFSET,
    currentNodeType: null,
    isDragging: false
  });
  const { currentPos, elementOffset, currentNodeType, isDragging } = state;
  const debouncedIsDragging = useDebounce(isDragging, 100);
  const debouncedCurrentPos = useDebounce(currentPos, 100);
  const pluginRef = useRef(null);
  const { refs, floatingStyles } = useFloating({
    open: true,
    middleware: [
      offset(elementOffset)
    ],
    placement: "left-start",
    transform: true
  });
  const updateState = (newState) => {
    setState((prevState) => ({
      ...prevState,
      ...newState
    }));
  };
  const handleMouseMove = (e) => {
    if (!editor.isEditable) return;
    if (e.target instanceof HTMLElement && e.target.closest("[data-diff-display]")) {
      updateState({
        currentPos: null,
        elementOffset: DEFAULT_OFFSET,
        currentNodeType: null
      });
      return;
    }
    const blockParent = getNodeAtPos(editor, e.clientX, e.clientY);
    if (!blockParent) return;
    const { node: element } = editor.view.domAtPos(blockParent.start);
    if (!(element instanceof HTMLElement)) return;
    updateState({
      currentPos: blockParent.start,
      elementOffset: getElementOffset(blockParent.node, editor, blockParent.start),
      currentNodeType: blockParent.node.type.name
    });
    refs.setReference(element);
  };
  const handleMouseLeave = (e) => {
    const newTarget = "toElement" in e ? e.toElement : e.relatedTarget;
    if (newTarget instanceof HTMLElement && newTarget.closest("[data-drag-handle]")) {
      return;
    }
    resetDragHandleState();
  };
  const resetDragHandleState = () => {
    {
      cleanupDragPreviews();
    }
    updateState({
      currentPos: null,
      elementOffset: DEFAULT_OFFSET,
      currentNodeType: null
    });
  };
  useClientLayoutEffect(() => {
    if (isDragging) return;
    const handleScroll = () => {
      resetDragHandleState();
    };
    editor.view.dom.addEventListener("mousemove", handleMouseMove);
    editor.view.dom.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("scroll", handleScroll, true);
    const observer = new MutationObserver(resetDragHandleState);
    observer.observe(editor.view.dom, {
      childList: true,
      subtree: true
    });
    return () => {
      editor.view.dom.removeEventListener("mousemove", handleMouseMove);
      editor.view.dom.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll, true);
      observer.disconnect();
    };
  }, [
    editor,
    isDragging
  ]);
  useClientLayoutEffect(() => {
    if (editor.isDestroyed) {
      return () => {
        pluginRef.current = null;
      };
    }
    if (!pluginRef.current) {
      pluginRef.current = DragHandlePlugin();
      editor.registerPlugin(pluginRef.current);
    }
    return () => {
      editor.unregisterPlugin(DragHandlePluginKey);
      pluginRef.current = null;
    };
  }, [
    editor
  ]);
  const setupDragPreview = (e, pos) => {
    if (!editor.isEditable || !e.dataTransfer) {
      return false;
    }
    const resolved = editor.state.doc.resolve(pos);
    const { node: element } = editor.view.domAtPos(resolved.start());
    if (!(element instanceof HTMLElement)) {
      return false;
    }
    const container = createDragPreviewContainer();
    const nodeType = resolved.node().type.name;
    const previewContent = createDragPreview(element, editor.view.dom, nodeType);
    if (!previewContent) {
      return false;
    }
    container.appendChild(previewContent);
    container.style.paddingLeft = "1rem";
    document.body.appendChild(container);
    configureDragData(e, container);
    return true;
  };
  const createDragPreviewContainer = () => {
    cleanupDragPreviews();
    const container = document.createElement("div");
    container.dataset.dragHandlePreview = "true";
    container.style.position = "absolute";
    container.style.left = "-10000px";
    container.style.top = "-10000px";
    container.style.pointerEvents = "none";
    container.style.padding = "0.1rem";
    return container;
  };
  const configureDragData = (e, container) => {
    e.dataTransfer.clearData();
    e.dataTransfer.setData("text/plain", container.innerHTML);
    e.dataTransfer.setData("text/html", container.innerHTML);
    e.dataTransfer.setDragImage(container, 0, 0);
  };
  const handleOnDragStart = (e) => {
    if (!currentPos) {
      return;
    }
    updateState({
      isDragging: true
    });
    const setupSuccess = setupDragPreview(e, currentPos);
    if (!setupSuccess) {
      updateState({
        isDragging: false
      });
      return;
    }
    setupEditorSelection(currentPos, e.ctrlKey);
    setupDragCleanup();
  };
  const setupEditorSelection = (pos, isCtrlKey) => {
    const { tr } = editor.view.state;
    const resolved = editor.state.doc.resolve(pos);
    const start = resolved.node().type.name === "codeBlock" ? resolved.start() - 1 : resolved.start();
    const selection = CustomTextSelection.create(editor.state.doc, start, resolved.end());
    const slice = selection.content();
    editor.view.dragging = {
      slice,
      move: isCtrlKey
    };
    tr.setSelection(selection);
    editor.view.dispatch(tr);
  };
  const setupDragCleanup = () => {
    const cleanupDrag = () => {
      {
        cleanupDragPreviews();
      }
      updateState({
        isDragging: false,
        currentPos: null
      });
    };
    document.addEventListener("drop", cleanupDrag, {
      once: true
    });
    document.addEventListener("dragend", cleanupDrag, {
      once: true
    });
  };
  const onDragEnd = () => {
    updateState({
      isDragging: false,
      currentPos: null
    });
  };
  const handleOnMouseDown = () => {
    const inlineToolbar = document.querySelector(".tiptap-inline-toolbar");
    if (inlineToolbar) {
      const event = new MouseEvent("mousedown", {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0
      });
      inlineToolbar.dispatchEvent(event);
    }
  };
  if (!currentPos || !debouncedCurrentPos) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Handle, {
    ref: refs.setFloating,
    style: {
      ...floatingStyles,
      ...debouncedIsDragging && isDragging && {
        opacity: 0,
        pointerEvents: "none"
      }
    },
    "data-drag-handle": true,
    onDragStart: handleOnDragStart,
    onDragEnd,
    onMouseDown: handleOnMouseDown,
    draggable: true,
    children: [
      /* @__PURE__ */ jsx(DragButton, {
        size: "xss",
        children: /* @__PURE__ */ jsx(IconOther12DragHandle, {})
      }),
      currentNodeType && IS_DEBUG
    ]
  }, currentPos);
};

export { DragHandle };
//# sourceMappingURL=DragHandle.js.map
